import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { tenantService } from '../services/api';
import {
  validateCNPJ, maskCNPJ, fetchCNPJ, mapCNPJData
} from '../utils/validators';

/* ── Login ─────────────────────────────────────────────────────────── */
export function Login() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">🚽</div>
          <h1>SaniTech</h1>
          <p>Gestão de Banheiros Químicos</p>
        </div>
        <form onSubmit={handle}>
          <div className="form-group">
            <label className="form-label">E-mail</label>
            <input className="form-control" type="email" required placeholder="seu@email.com"
              value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Senha</label>
            <input className="form-control" type="password" required placeholder="••••••••"
              value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
          </div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
        <p style={{ marginTop: 16, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>
          Não tem conta?{' '}
          <Link to="/register" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 500 }}>
            Cadastre sua empresa
          </Link>
        </p>
      </div>
    </div>
  );
}

/* ── Register ──────────────────────────────────────────────────────── */
export function Register() {
  const [form, setForm] = useState({
    company_name: '', cnpj: '', email: '', phone: '',
    admin_name: '', admin_email: '', admin_password: '',
  });
  const [cnpjStatus, setCnpjStatus] = useState(''); // '', 'loading', 'ok', 'error', 'invalid'
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  /* CNPJ: máscara + validação + consulta Receita */
  const handleCNPJ = async (e) => {
    const masked = maskCNPJ(e.target.value);
    setForm(f => ({ ...f, cnpj: masked }));
    const raw = masked.replace(/\D/g, '');

    if (raw.length < 14) { setCnpjStatus(''); return; }

    if (!validateCNPJ(raw)) {
      setCnpjStatus('invalid');
      toast.error('CNPJ inválido');
      return;
    }

    setCnpjStatus('loading');
    try {
      const data = await fetchCNPJ(raw);
      const mapped = mapCNPJData(data);
      setForm(f => ({ ...f, ...mapped, cnpj: masked }));
      setCnpjStatus('ok');
      toast.success('Dados preenchidos automaticamente pela Receita Federal!');
    } catch {
      setCnpjStatus('error');
      toast('CNPJ válido, mas não encontrado na Receita Federal.', { icon: '⚠️' });
    }
  };

  const handle = async (e) => {
    e.preventDefault();
    if (cnpjStatus === 'invalid') return toast.error('Corrija o CNPJ antes de continuar');
    if (!form.company_name) return toast.error('Razão Social é obrigatória');
    setLoading(true);
    try {
      const payload = {
        name: form.company_name,
        cnpj: form.cnpj,
        email: form.email,
        phone: form.phone,
        admin_name: form.admin_name,
        admin_email: form.admin_email,
        admin_password: form.admin_password,
      };
      const res = await tenantService.register(payload);
      const { access_token, user } = res.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      toast.success('Empresa cadastrada com sucesso!');
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || 'Erro de validação');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Erro ao cadastrar');
      }
    } finally {
      setLoading(false);
    }
  };

  const cnpjBorder = {
    '': {},
    loading: { borderColor: '#f59e0b' },
    ok: { borderColor: '#22c55e' },
    error: { borderColor: '#f59e0b' },
    invalid: { borderColor: '#ef4444' },
  }[cnpjStatus];

  const cnpjHint = {
    loading: '🔄 Consultando Receita Federal...',
    ok: '✅ Dados preenchidos automaticamente',
    error: '⚠️ CNPJ válido, mas não encontrado',
    invalid: '❌ CNPJ inválido',
  }[cnpjStatus];

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 540 }}>
        <div className="login-logo">
          <div className="login-logo-icon">🚽</div>
          <h1>Cadastre sua Empresa</h1>
          <p>Crie sua conta no SaniTech</p>
        </div>
        <form onSubmit={handle}>
          <p className="form-section-label">Dados da Empresa</p>

          <div className="form-group">
            <label className="form-label">CNPJ *</label>
            <input
              className="form-control"
              required
              placeholder="00.000.000/0001-00"
              value={form.cnpj}
              onChange={handleCNPJ}
              style={cnpjBorder}
              maxLength={18}
            />
            {cnpjHint && <small style={{ color: cnpjStatus === 'invalid' ? '#ef4444' : '#6b7280', fontSize: 12 }}>{cnpjHint}</small>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Razão Social *</label>
              <input className="form-control" required value={form.company_name}
                onChange={set('company_name')} placeholder="Nome da empresa" />
            </div>
            <div className="form-group">
              <label className="form-label">Nome Fantasia</label>
              <input className="form-control" value={form.trade_name || ''}
                onChange={set('trade_name')} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">E-mail comercial</label>
              <input className="form-control" type="email" value={form.email} onChange={set('email')} />
            </div>
            <div className="form-group">
              <label className="form-label">Telefone</label>
              <input className="form-control" value={form.phone} onChange={set('phone')} />
            </div>
          </div>

          <p className="form-section-label" style={{ marginTop: 12 }}>Usuário Administrador</p>

          <div className="form-group">
            <label className="form-label">Nome completo *</label>
            <input className="form-control" required value={form.admin_name} onChange={set('admin_name')} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">E-mail *</label>
              <input className="form-control" type="email" required value={form.admin_email} onChange={set('admin_email')} />
            </div>
            <div className="form-group">
              <label className="form-label">Senha *</label>
              <input className="form-control" type="password" required minLength={6}
                value={form.admin_password} onChange={set('admin_password')} />
            </div>
          </div>

          <button className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', marginTop: 12 }}
            disabled={loading || cnpjStatus === 'loading' || cnpjStatus === 'invalid'}>
            {loading ? 'Cadastrando...' : 'Criar conta'}
          </button>
        </form>
        <p style={{ marginTop: 16, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>
          Já tem conta?{' '}
          <Link to="/login" style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 500 }}>Entrar</Link>
        </p>
      </div>
    </div>
  );
}
